import { Link } from "react-router";
import { Moon, Sun, Settings, LogOut } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
} from "@headerhawk/ui";
import { useTheme } from "@/components/providers";
import { useSignOut } from "@/lib/hooks/use-sign-out";
import { isDemoTab } from "@/lib/auth/impersonation";
import { useSiteScope, withSiteScope } from "@/lib/hooks/use-site-scope";

interface UserMenuProps {
  user: {
    email?: string;
    name?: string;
    picture?: string;
  };
}

export function UserMenu({ user }: UserMenuProps) {
  // The resolved theme, not the stored one: on "system" the stored value says
  // nothing about what is on screen, so the item would offer the mode already
  // showing on a dark OS and clicking it would change nothing.
  const { resolvedTheme, setTheme } = useTheme();
  const signOut = useSignOut();
  const { siteId: siteScope } = useSiteScope();

  const initials = user.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : (user.email?.slice(0, 2).toUpperCase() ?? "??");

  // Shared with onboarding's AccountGreeting, so both clear the same state
  // (see useSignOut).
  const handleLogout = signOut;

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-8 w-8 rounded-full"
          data-testid="user-menu-button"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={user.picture} alt={user.name ?? user.email} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            {user.name && (
              <p className="text-sm font-medium leading-none">{user.name}</p>
            )}
            {user.email && (
              <p className="text-xs leading-none text-muted-foreground">
                {user.email}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={toggleTheme}>
          {resolvedTheme === "dark" ? (
            <Sun className="mr-2 h-4 w-4" />
          ) : (
            <Moon className="mr-2 h-4 w-4" />
          )}
          <span>{resolvedTheme === "dark" ? "Light mode" : "Dark mode"}</span>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={withSiteScope("/dashboard/settings", siteScope)}>
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {/* In the public demo (task 405) signing out ends the demo's
              session, and the tab shows the demo's ended page. */}
          <span>{isDemoTab() ? "End the demo" : "Log out"}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
